import {
  type ApiError,
  type ApiInfinitePaginatedResponse,
  type ApiPaginatedResponse,
} from "@/api/_client";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";
import { clsx, type ClassValue } from "clsx";
import { isValid, parse } from "date-fns";
import { enUS } from "date-fns/locale";
import React, { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Consistent, locale-aware date format strings for the app */
export const DATE_FORMAT = "PP";
export const DATETIME_FORMAT = "PPp";

export function toTitleCase(str: string): string {
  return str
    .replace(/[_-]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

type ConditionalWrapperProps = {
  condition: boolean;
  wrapper: (children: ReactNode) => ReactNode;
  children: ReactNode;
};

export const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
}: ConditionalWrapperProps) => (condition ? wrapper(children) : children);

export const randomId = () => Math.random().toString(36).substring(2, 15);

// Accepts either structured input (first, last) or a single free-form string
// (e.g. an org name); whitespace inside any arg is treated as word boundaries
// so `initials("Common Forge")` and `initials("Common", "Forge")` both → "CF".
export const initials = (
  ...parts: (string | null | undefined)[]
): string => {
  const tokens = parts
    .flatMap((p) => (p ?? "").trim().split(/\s+/))
    .filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[1][0]).toUpperCase();
};

export const isDateValid = (date: string) => {
  if (!date) return false;
  const parsedDate = parse(date, "P", new Date(), { locale: enUS });
  return isValid(parsedDate);
};

export const ensureHttps = (url: string) => {
  if (!url) {
    return "";
  }
  if (!url.match(/^[a-zA-Z]+:\/\//)) {
    return "https://" + url;
  }
  return url;
};

export const generateSortString = (
  sorting: { id: string; desc: boolean }[],
) => {
  return sorting.map((s) => `${s.desc ? "-" : ""}${s.id}`).join(",");
};

export const generateSorting = (sortString: string) => {
  return {
    id: sortString.replace("-", ""),
    desc: sortString.startsWith("-"),
  };
};

export const formatNumberWithSuffix = (num: number): string => {
  if (num >= 1000000000) {
    return (
      ((num / 1000000000) % 1 === 0
        ? (num / 1000000000).toFixed(0)
        : (num / 1000000000).toFixed(1)) + "MM"
    );
  } else if (num >= 1000000) {
    return (
      ((num / 1000000) % 1 === 0
        ? (num / 1000000).toFixed(0)
        : (num / 1000000).toFixed(1)) + "M"
    );
  } else if (num >= 1000) {
    return (
      ((num / 1000) % 1 === 0
        ? (num / 1000).toFixed(0)
        : (num / 1000).toFixed(1)) + "K"
    );
  } else {
    return num.toString();
  }
};

/**
 * Parses a string or Date object into a Date object.
 * It can handle the following formats:
 * - Standard date format, e.g., "2024-05-01"
 * - Quarter format, e.g., "2024Q2"
 * - Date object
 *
 * @param input - The input string or Date object to parse.
 * @returns The parsed Date object, or undefined if the input is invalid.
 */
export const parseDate = (
  input: string | Date | undefined | null,
): Date | undefined => {
  if (input === undefined || input === null) {
    return undefined;
  }

  // Convert input to string if it's a Date instance
  const dateString = input instanceof Date ? input.toISOString() : input;

  // Check for quarter format, e.g., "2024Q2"
  const quarterMatch = dateString.match(/^(\d{4})Q([1-4])$/);
  if (quarterMatch) {
    const year = parseInt(quarterMatch[1], 10);
    const quarter = parseInt(quarterMatch[2], 10);
    const month = (quarter - 1) * 3; // Convert quarter to starting month (0-indexed)
    return new Date(year, month);
  }

  // Check for standard date format and parse directly
  const parsedDate = new Date(dateString);
  if (!isNaN(parsedDate.getTime())) {
    return parsedDate;
  }

  // If none of the formats matched and it's not a valid date
  return undefined;
};

export const addQueryParam = (
  path: string,
  key: string,
  value: string | string[],
): string => {
  const urlObj = new URL(path, "http://dummy.base");
  const params = new URLSearchParams(urlObj.search);
  params.set(key, value as string);
  urlObj.search = params.toString();
  return urlObj.pathname + urlObj.search;
};

export const isMicrosoftOfficeDocument = (fileName: string): boolean => {
  const officeExtensions: Set<string> = new Set([
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
  ]);

  const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();

  return officeExtensions.has(extension);
};

export type UseFetchAllReturn<T> = {
  results: T[];
  isFetching: boolean;
  modifyResults: (modifier: (results: T[]) => T[]) => void;
};

export function useFetchAllPages<T extends Record<string, any>>(
  partialData: UseInfiniteQueryResult<
    ApiInfinitePaginatedResponse<T>,
    ApiError<T>
  >,
): UseFetchAllReturn<T> {
  const [isFetching, setIsFetching] = React.useState<boolean>(true);
  const [modifiedResults, setModifiedResults] = React.useState<T[]>([]);

  React.useEffect(() => {
    if (partialData.hasNextPage && !partialData.isFetchingNextPage) {
      partialData.fetchNextPage?.();
    }

    if (
      !partialData.hasNextPage &&
      !partialData.isFetching &&
      !partialData.isFetchingNextPage
    ) {
      setIsFetching(false); // No more pages to fetch, fetching is done
    } else {
      setIsFetching(true); // Still fetching pages
    }
  }, [
    partialData.hasNextPage,
    partialData.isFetchingNextPage,
    partialData.isFetching,
    partialData.fetchNextPage,
  ]);

  // This memoized value combines all pages into a single array
  const combinedResults = React.useMemo((): T[] => {
    if (!partialData.data?.pages.length) {
      return []; // No data yet (initial load)
    }
    let mergedArray: T[] = [];
    partialData.data?.pages.forEach((page: ApiPaginatedResponse<T>) => {
      if (page) mergedArray = mergedArray.concat(page.results);
    });
    return mergedArray;
  }, [partialData.data]);

  // Apply modifications to combinedResults
  React.useEffect(() => {
    setModifiedResults(combinedResults);
  }, [combinedResults]);

  const modifyResults = (modifier: (results: T[]) => T[]) => {
    setModifiedResults(modifier(modifiedResults));
  };

  return { results: modifiedResults, isFetching, modifyResults };
}

export const isDate = (value: string) => {
  return !isNaN(Date.parse(value));
};

export const isNoRender = (filename: string): boolean => {
  const extension = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  const NO_RENDER = [".zip"];
  return NO_RENDER.includes(extension);
};

export const getSlug = (): string | null => {
  if (typeof window === "undefined") return null;

  const host = window.location.hostname;
  const parts = host.split(".");

  if (parts.length < 3) return null;

  return parts[0];
};

export function stringToPastelColor(input: string): string {
  function hashCode(str: string): number {
    return str.split("").reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
  }

  const hash = hashCode(input);
  const hue = Math.abs(hash) % 360; // Ensures hue is within 0-360
  const saturation = 50 + (Math.abs(hash) % 30); // 50-80% for variety
  const lightness = 70 + (Math.abs(hash) % 10); // 70-80% variation for better contrast

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export const useTypingDetection = (
  value: string,
  delay: number = 1000,
): [boolean, () => void] => {
  const [isTyping, setIsTyping] = React.useState<boolean>(false);

  const previousValue = React.useRef<string>(value);
  const isInitialized = React.useRef<boolean>(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isManuallyReset = React.useRef<boolean>(false);

  // Helper function to clear timeout
  const clearTypingTimeout = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Manual reset function
  const resetTyping = React.useCallback(() => {
    setIsTyping(false);
    previousValue.current = value;
    clearTypingTimeout();
    isManuallyReset.current = true;
  }, [value, clearTypingTimeout]);

  // Main typing detection effect
  React.useEffect(() => {
    // Initialize on first render
    if (!isInitialized.current) {
      isInitialized.current = true;
      previousValue.current = value;
      return;
    }

    // Skip detection if manually reset
    if (isManuallyReset.current) {
      isManuallyReset.current = false;
      previousValue.current = value;
      return;
    }

    // Detect value changes
    if (previousValue.current !== value) {
      setIsTyping(true);
      clearTypingTimeout();

      timeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        timeoutRef.current = null;
      }, delay);

      previousValue.current = value;
    }
  }, [value, delay, clearTypingTimeout]);

  React.useEffect(() => {
    return clearTypingTimeout;
  }, [clearTypingTimeout]);

  return [isTyping, resetTyping];
};

/**
 * Downloads a blob as a file using the browser's download functionality
 * @param blob - The blob data to download
 * @param filename - The name for the downloaded file
 * @param contentType - The MIME type of the file (optional)
 */
export const downloadBlob = (
  blob: Blob,
  filename: string,
  contentType?: string
): void => {
  // Create a new blob with the specified content type if provided
  const downloadBlob = contentType
    ? new Blob([blob], { type: contentType })
    : blob;

  const downloadUrl = window.URL.createObjectURL(downloadBlob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Formats a number as currency with appropriate decimals
 * @param value - The numeric value to format
 * @param currency - The currency code (default: USD)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
};

/**
 * Formats a number with thousand separators
 * @param value - The numeric value to format
 * @returns Formatted number string
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("en-US").format(value);
};

/**
 * Formats bytes into a human-readable string
 * @param bytes - The number of bytes
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatBytes = (bytes: number, decimals: number = 1): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Copies text to the clipboard, resolving to true on success and false on
 * failure. `navigator.clipboard` is only available on secure origins (HTTPS or
 * localhost), so this falls back to a temporary-textarea + execCommand path for
 * insecure origins (e.g. HTTP local dev). Callers should only claim success —
 * e.g. show a "copied" toast — when this resolves true.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the legacy path below
    }
  }
  const textarea = document.createElement("textarea");
  try {
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
};
